// productDict.ts — слой чужих словарей (§15 задача L4): ленивая догрузка текста уведомлений
// продукта-источника по source_slug. Кеш в памяти на slug+locale (ключ несёт локаль — смена
// языка сама по себе адресует новую запись, отдельного сброса не нужно), дедуп параллельных
// обращений к одному слагу — общий inflight-промис. Ключа/словаря нет — фолбэк §15.6 решение
// владельца 80 («Источник удалён»), сырой ключ наружу не выходит никогда.
import { fetchProductLang } from "../api/productLang";
import { getLocale, subscribeLocale, t } from "./i18n";
import { interpolate } from "./format";
// MISS_TTL_MS — как долго держим фолбэк «источник удалён» без повторного запроса после промаха.
const MISS_TTL_MS = 60_000;
function isMissing(slot) {
    return slot.missing === true;
}
const store = new Map();
const inflight = new Map();
// Последний успешный словарь slug на любом языке: при смене языка держит прежний текст, пока не пришёл новый.
const lastDicts = new Map();
const listeners = new Set();
let revision = 0;
function keyOf(slug, locale) {
    return `${locale}\u0000${slug}`;
}
function bump() {
    revision += 1;
    listeners.forEach((cb) => cb());
}
// Локаль сменилась — старые ключи store просто перестают читаться (keyOf несёт locale),
// новая запись догрузится лениво по первому обращению; ревизию растим, чтобы читатели
// (useSyncExternalStore) увидели новый снимок и заново прочитали sync-геттеры ниже.
subscribeLocale(() => bump());
// ensureLoaded — один запрос на slug+locale: второй параллельный вызов получает тот же
// inflight-промис, а не новый fetch.
function ensureLoaded(slug) {
    const locale = getLocale();
    const key = keyOf(slug, locale);
    const existing = store.get(key);
    // Промах держим только MISS_TTL_MS — дальше пробуем снова, а не фолбэк навсегда.
    if (existing && !(isMissing(existing) && Date.now() - existing.at > MISS_TTL_MS)) {
        return Promise.resolve();
    }
    const running = inflight.get(key);
    if (running)
        return running;
    const p = fetchProductLang(slug, locale)
        .then((res) => {
        // запрос сброшен invalidateProductDicts — его ответ уже устарел
        if (inflight.get(key) !== p)
            return;
        if (res.status === "ok") {
            store.set(key, { dict: res.data.dict });
            lastDicts.set(slug, res.data.dict);
        }
        else {
            store.set(key, { missing: true, at: Date.now() });
        }
    })
        .catch(() => {
        if (inflight.get(key) === p)
            store.set(key, { missing: true, at: Date.now() });
    })
        .finally(() => {
        if (inflight.get(key) === p)
            inflight.delete(key);
        bump();
    });
    inflight.set(key, p);
    return p;
}
// invalidateProductDicts — продукт обновился (resync, product.state ok): словарь мог получить новые
// ключи. Кеш и запросы в полёте сбрасываем, прежний текст (lastDicts) держится до прихода нового.
export function invalidateProductDicts(slugs) {
    const set = new Set(slugs);
    for (const key of [...store.keys()]) {
        if (set.has(key.slice(key.indexOf("\u0000") + 1)))
            store.delete(key);
    }
    for (const key of [...inflight.keys()]) {
        if (set.has(key.slice(key.indexOf("\u0000") + 1)))
            inflight.delete(key);
    }
}
export function subscribeProductDict(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}
export function getProductDictRevision() {
    return revision;
}
function readSlot(slug) {
    return store.get(keyOf(slug, getLocale()));
}
// notifyText — синхронное чтение уже загруженного словаря; первое обращение к новому slug
// запускает фоновую загрузку (ensureLoaded), до её завершения (и при промахе) отдаёт фолбэк.
export function notifyText(sourceSlug, key, params) {
    ensureLoaded(sourceSlug);
    const slot = readSlot(sourceSlug);
    if (slot && !isMissing(slot)) {
        const raw = slot.dict[key];
        if (raw !== undefined)
            return { text: interpolate(raw, params), known: true };
    }
    return { text: t("shell.notify.source_removed"), known: false };
}
// productTitle — название источника для группировки/фильтра; undefined пока словарь не пришёл,
// продукта или ключа "<slug>.product.title" нет (кит в этом случае сам показывает «Платформа»).
export function productTitle(sourceSlug) {
    ensureLoaded(sourceSlug);
    const slot = readSlot(sourceSlug);
    if (!slot || isMissing(slot))
        return undefined;
    return slot.dict[`${sourceSlug}.product.title`];
}
// loadProductDicts — один параллельный проход по словарям продуктов текущего языка; не отказывает
// (промах словаря — штатный, ensureLoaded его глотает).
export async function loadProductDicts(slugs) {
    await Promise.all([...new Set(slugs)].map(ensureLoaded));
}
// productText — синхронное чтение текста паспорта по ключу; undefined — словаря или ключа нет.
// Загрузку не запускает (её делает loadProductDicts). Пока словарь нового языка не пришёл,
// отдаёт текст прежнего, чтобы подписи не мигали.
export function productText(slug, key) {
    const slot = readSlot(slug);
    if (!slot)
        return lastDicts.get(slug)?.[key];
    return isMissing(slot) ? undefined : slot.dict[key];
}
// Слой словарей для контекста экрана: один и тот же у меню продукта и у ящика профиля.
export const productTextApi = { load: loadProductDicts, text: productText };
export function resolveProductLabels(products, text = productText) {
    return products.map((p) => {
        const fallback = p.role === "profile" ? "shell.menu.untitled" : "shell.product.default_title";
        return {
            ...p,
            label: text(p.product, p.label) ?? t(fallback),
            ...(p.tabs ? { tabs: p.tabs.map((tab) => ({ ...tab, title: text(p.product, tab.title) ?? t("shell.menu.untitled") })) } : {}),
        };
    });
}
function resolveItem(it, slug, text) {
    return {
        ...it,
        label: text(slug, it.label) ?? t("shell.menu.untitled"),
        ...(it.submenu ? { submenu: resolveMenu(it.submenu, slug, text) } : {}),
        ...(it.children ? { children: it.children.map((c) => resolveItem(c, slug, text)) } : {}),
    };
}
export function resolveMenu(node, slug, text = productText) {
    return {
        ...node,
        title: node.title ? text(slug, node.title) : undefined,
        sections: node.sections?.map((s) => ({
            ...s,
            title: s.title ? text(slug, s.title) : undefined,
            items: s.items.map((it) => resolveItem(it, slug, text)),
        })),
    };
}
// Асинхронные пары для не-реактивных мест (getSources()/getHistory() в historyApi Frame.tsx идут
// через Promise уже по контракту экрана — ждём загрузку явно, без фолбэка «пока грузится»).
export async function notifyTextAsync(sourceSlug, key, params) {
    await ensureLoaded(sourceSlug);
    return notifyText(sourceSlug, key, params);
}
export async function productTitleAsync(sourceSlug) {
    await ensureLoaded(sourceSlug);
    return productTitle(sourceSlug);
}
